/**
 * 画布视图（M1-13 / DESIGN.md §7.1、§7.4）。
 *
 * 职责只有两件：把 DOM 事件翻译成视图命令，把当前视口渲染出来。
 *
 * **分层约束**（CLAUDE.md 规则 4）：UI 层不得直接引用 TileStore 或 GPU backend。
 * 本文件只 import 交互层的 `viewport.ts`——那是纯计算，没有 DOM 也没有 GPU。
 * 真正的分块渲染由应用层经 `onRender` 注入；不注入时画一个占位棋盘格，
 * 这样视口交互本身可以独立于渲染管线被测试与调试。
 *
 * 手感上的三条，都是照 Photoshop 来的（§7.4）：
 * - 空格临时抓手，松开回到原工具
 * - Alt+滚轮缩放，且**以光标位置为锚点**
 * - Ctrl+0 适应窗口、Ctrl+1 实际像素
 *
 * 标尺（Ctrl+R 开关，跟 PS 一致）：顶/左各一条，刻度画在文档坐标系里，
 * 随视口缩放/平移重算——这也是复用 documentToScreen/screenToDocument 而
 * 不是自己另写一套换算的原因。刻度定位用 `documentToScreen` 逐点投影，
 * 旋转视图（viewport.rotation ≠ 0）时刻度间距会不再均匀，这是已知的
 * 简化：真正跟着旋转走的标尺需要斜着画数字，收益跟复杂度不成比例，
 * 而旋转视图本身是个用得很少的功能。
 */
import type { JSX } from 'solid-js';
import { For, Show, createEffect, createSignal, onCleanup, onMount } from 'solid-js';
import {
  HandTool,
  applyViewCommand,
  documentBoundsOnScreen,
  documentToScreen,
  fitToWindow,
  screenToDocument,
  viewCommandForKey,
  viewCommandForWheel,
  type DocumentPoint,
  type Size,
  type ViewCommand,
  type Viewport,
} from '../../interaction/viewport';
import type { Guide } from '../../document/types';
import styles from './CanvasView.module.css';

/** 棋盘格方块边长（CSS 像素）。画在 canvas 里，不是 CSS 尺寸。 */
const CHECKER_CELL = 8;

/** 标尺条厚度（CSS 像素）——跟 Photoshop 的默认粗细接近。 */
const RULER_SIZE = 20;

/** 标尺主刻度之间至少留这么多屏幕像素，太密会挤成一团黑线。 */
const RULER_MIN_MAJOR_GAP = 50;

/**
 * 选一个"整数刻度"（文档像素），使其屏幕间距落在 `RULER_MIN_MAJOR_GAP`
 * 附近——1/2/5/10 这几个台阶是刻度尺的标准惯例（跟时钟表盘一个道理，
 * 缩放到任何比例都能找到一个不太挤也不太稀的刻度密度）。
 */
function rulerMajorStep(scale: number): number {
  const raw = RULER_MIN_MAJOR_GAP / scale;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  for (const mult of [1, 2, 5, 10]) {
    if (mult * magnitude >= raw) return mult * magnitude;
  }
  return 10 * magnitude;
}

/**
 * 棋盘格图案，按颜色缓存。
 *
 * 每帧重建 pattern 会重新分配一张离屏画布，缓存后整个绘制只剩一次 fillRect。
 * 键用两个颜色值——主题切换时颜色变了，自然会建新的。
 */
const patternCache = new Map<string, CanvasPattern | null>();

function checkerPattern(
  ctx: CanvasRenderingContext2D,
  colorA: string,
  colorB: string,
): CanvasPattern | null {
  const key = `${colorA}|${colorB}`;
  const cached = patternCache.get(key);
  if (cached !== undefined) return cached;

  const tile = document.createElement('canvas');
  tile.width = CHECKER_CELL * 2;
  tile.height = CHECKER_CELL * 2;
  const tileCtx = tile.getContext('2d');
  let pattern: CanvasPattern | null = null;
  if (tileCtx !== null) {
    tileCtx.fillStyle = colorA;
    tileCtx.fillRect(0, 0, CHECKER_CELL * 2, CHECKER_CELL * 2);
    tileCtx.fillStyle = colorB;
    tileCtx.fillRect(CHECKER_CELL, 0, CHECKER_CELL, CHECKER_CELL);
    tileCtx.fillRect(0, CHECKER_CELL, CHECKER_CELL, CHECKER_CELL);
    pattern = ctx.createPattern(tile, 'repeat');
  }
  patternCache.set(key, pattern);
  return pattern;
}

/** 视口序列化成 DOM 属性。空格分隔的四个数：scale rotation tx ty。 */
function serializeViewport(viewport: Viewport): string {
  return `${viewport.scale} ${viewport.rotation} ${viewport.tx} ${viewport.ty}`;
}

export interface CanvasViewProps {
  /** 文档尺寸（文档像素）。 */
  readonly documentSize: Size;
  /**
   * 实际渲染回调，由应用层注入。
   *
   * UI 层不认识 TileStore 与 GPU backend，只把「现在的视口是什么」告诉上层。
   * 返回 false 表示上层没有渲染，本组件退回占位绘制。
   */
  readonly onRender?: (canvas: HTMLCanvasElement, viewport: Viewport, view: Size) => boolean;
  /** 视口变化通知，用于状态栏显示缩放比例等。 */
  readonly onViewportChange?: (viewport: Viewport) => void;
  /**
   * 内容版本号。文档内容变化（视口不变）时递增它来触发重绘——
   * UI 层不订阅文档，只认这个数字。
   */
  readonly renderVersion?: number;
  /**
   * 工具指针事件（文档坐标）。抓手（空格）激活时不触发——
   * 平移优先于一切工具，这是 Photoshop 的行为。
   * dblclick 时 phase 为 'double'（多边形套索闭合等用）。
   * `samples` 展开了 coalesced 事件（数位板采样率高于事件派发率），
   * 每个样本带压感（无压感设备为 null）与时间戳。
   */
  readonly onToolPointer?: (
    phase: 'down' | 'move' | 'up' | 'double',
    point: DocumentPoint,
    modifiers: { readonly shiftKey: boolean; readonly altKey: boolean },
    samples: ReadonlyArray<DocumentPoint & { pressure: number | null; timeMs: number }>,
  ) => void;
  /**
   * 当前工具的 CSS cursor 值（含吸管的取色圆环这类数据 URI）。
   * 抓手激活时忽略——平移的 grab/grabbing 由本组件自己的 CSS 类管，
   * 优先级不能被外部这个值盖掉。
   */
  readonly cursor?: string;
  /** 显示顶/左标尺（Ctrl+R 开关）。缺省显示，跟 §7.4 的默认手感一致。 */
  readonly showRulers?: boolean;
  /**
   * 缩放工具是否是当前激活工具。为真时点击画布直接以点击处为锚点缩放
   * 一档，不走 onToolPointer（那条管线是给内容编辑工具用文档坐标的）。
   */
  readonly zoomToolActive?: boolean;
  /** 缩放工具属性栏的放大/缩小切换（缺省放大；按住 Alt 点击时方向反过来，跟 PS 一致）。 */
  readonly zoomDirection?: 'in' | 'out';
  /**
   * 抓手工具是否是当前激活工具。为真时等同于一直按着空格——拖拽直接
   * 平移，不用先按住空格。跟真实空格临时抓手共用同一个 `HandTool` 状态机
   * （见下方 `syncHandSpace`），两路只是"按空格"和"选中了抓手工具"两种
   * 让它保持按下状态的方式，行为完全一致。
   */
  readonly handToolActive?: boolean;
  /**
   * "视图"菜单点出来的缩放命令（放大/缩小/按屏幕大小缩放/实际像素）。
   * Ctrl+0/1/+/- 这几个键已经在本组件自己的 keydown 监听器里直接处理
   * （见下方 `onKeyDown`），不走 action registry——这个 prop 是给菜单点击
   * 这一条**额外**的路，两条路殊途同归都调 `applyViewCommand`，不会重复
   * 处理同一次按键。`seq` 每次请求自增，即使连续两次相同的命令
   * （比如连点两次"放大"）也能被 `createEffect` 认出"这是新的一次"。
   */
  readonly viewCommandRequest?: { readonly command: ViewCommand; readonly seq: number } | undefined;
  /** 参考线（「视图」菜单，DESIGN.md §6.5 补充）。不传等同于空数组。 */
  readonly guides?: readonly Guide[];
  /** 全局锁定开关（真实 PS 是全局的，不是逐条锁——见 `app/guides.ts`）。 */
  readonly guidesLocked?: boolean;
  /** 从标尺拖出一条新参考线并释放在画布内时触发。 */
  readonly onGuideCreate?: (axis: 'horizontal' | 'vertical', position: number) => void;
  /** 拖动一条已存在的参考线（按 `guides` 数组下标定位）。 */
  readonly onGuideMove?: (index: number, position: number) => void;
  /** 把一条已存在的参考线拖回标尺/拖出画布，删除它。 */
  readonly onGuideRemove?: (index: number) => void;
  /**
   * 画布区域右键（参照 Photoshop：选框/套索等选区工具右键弹出"取消选择/
   * 反选/羽化…"这类快捷菜单）。传视口坐标（`clientX/Y`），跟图层面板
   * 右键菜单的定位方式一致，装配层自己决定菜单内容。会 `preventDefault`
   * 挡掉浏览器原生右键菜单。
   */
  readonly onContextMenu?: ((position: { readonly x: number; readonly y: number }) => void) | undefined;
}

export function CanvasView(props: CanvasViewProps): JSX.Element {
  // 用回调 ref 而不是 `let x!: T` + `ref={x}`：后者的赋值由 Solid 编译器
  // 生成，ESLint 的 no-unassigned-vars 看不见，会报"永远是 undefined"
  let container: HTMLDivElement | undefined;
  let canvas: HTMLCanvasElement | undefined;
  let rulerH: HTMLCanvasElement | undefined;
  let rulerV: HTMLCanvasElement | undefined;

  const [viewport, setViewport] = createSignal<Viewport>({
    scale: 1,
    rotation: 0,
    tx: 0,
    ty: 0,
  });
  const [viewSize, setViewSize] = createSignal<Size>({ width: 0, height: 0 });
  const [handActive, setHandActive] = createSignal(false);
  /** 指针在画布区（非标尺）里的局部坐标——标尺上跟手的位置指示线用。离开画布置 null。 */
  const [hoverLocal, setHoverLocal] = createSignal<{ x: number; y: number } | null>(null);

  /**
   * 参考线拖拽中的状态。`index === null` 表示正从标尺往外拖一条全新的；
   * 否则是在拖 `guides[index]`。`screenPos` 是拖拽过程中跟手的局部坐标
   * （容器坐标系），释放时换算成文档坐标才提交。
   */
  const [guideDrag, setGuideDrag] = createSignal<{
    axis: 'horizontal' | 'vertical';
    index: number | null;
    screenPos: number;
    insideContainer: boolean;
  } | null>(null);

  const hand = new HandTool();
  // 物理空格键是否按下——跟"选中了抓手工具"是两回事，两者都能让 hand 保持
  // 按下态，任何一个松开都不该打断另一个
  let physicalSpaceDown = false;
  const syncHandSpace = (): void => {
    hand.setSpace(physicalSpaceDown || props.handToolActive === true);
    setHandActive(hand.active);
  };
  createEffect(() => {
    void props.handToolActive;
    syncHandSpace();
  });

  const context = (): { documentSize: Size; viewSize: Size } => ({
    documentSize: props.documentSize,
    viewSize: viewSize(),
  });

  const update = (next: Viewport): void => {
    setViewport(next);
    props.onViewportChange?.(next);
  };

  // "视图"菜单点出来的缩放命令：只在 seq 真的变了（新的一次请求）才应用，
  // 避免 props 因为其它原因重新求值时把同一个命令又套用一遍
  let lastViewCommandSeq = -1;
  createEffect(() => {
    const request = props.viewCommandRequest;
    if (request === undefined || request.seq === lastViewCommandSeq) return;
    lastViewCommandSeq = request.seq;
    update(applyViewCommand(viewport(), request.command, context()));
  });

  /** 画布区左上角为原点的坐标。事件里的 clientX/Y 是视口坐标，必须换算。 */
  const toLocal = (event: { clientX: number; clientY: number }): { x: number; y: number } => {
    const rect = container?.getBoundingClientRect();
    if (rect === undefined) return { x: event.clientX, y: event.clientY };
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  /** 事件坐标是否落在画布容器（标尺以外）区域内。 */
  const isInsideContainer = (event: { clientX: number; clientY: number }): boolean => {
    const rect = container?.getBoundingClientRect();
    if (rect === undefined) return false;
    return (
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom
    );
  };

  /* ---------------- 参考线拖拽 ---------------- */

  /** 从标尺往画布拖出一条新参考线。 */
  const onRulerPointerDown = (axis: 'horizontal' | 'vertical', event: PointerEvent): void => {
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    const local = toLocal(event);
    setGuideDrag({
      axis,
      index: null,
      screenPos: axis === 'horizontal' ? local.y : local.x,
      insideContainer: isInsideContainer(event),
    });
  };

  /** 拖动一条已存在的参考线（锁定时不响应）。 */
  const onGuideLinePointerDown = (index: number, guide: Guide, event: PointerEvent): void => {
    if (props.guidesLocked === true || guide.locked) return;
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    event.stopPropagation();
    const local = toLocal(event);
    setGuideDrag({
      axis: guide.axis,
      index,
      screenPos: guide.axis === 'horizontal' ? local.y : local.x,
      insideContainer: isInsideContainer(event),
    });
  };

  const onGuideDragPointerMove = (event: PointerEvent): void => {
    const drag = guideDrag();
    if (drag === null) return;
    const local = toLocal(event);
    setGuideDrag({
      ...drag,
      screenPos: drag.axis === 'horizontal' ? local.y : local.x,
      insideContainer: isInsideContainer(event),
    });
  };

  const onGuideDragPointerUp = (event: PointerEvent): void => {
    const drag = guideDrag();
    if (drag === null) return;
    (event.currentTarget as HTMLElement).releasePointerCapture(event.pointerId);
    setGuideDrag(null);
    const local = toLocal(event);
    const docPoint = screenToDocument(viewport(), local);
    const position = drag.axis === 'horizontal' ? docPoint.y : docPoint.x;
    if (drag.index === null) {
      // 新建：只有释放在画布区域内才算数，松在标尺/画布外则是取消
      if (isInsideContainer(event)) props.onGuideCreate?.(drag.axis, position);
      return;
    }
    if (isInsideContainer(event)) {
      props.onGuideMove?.(drag.index, position);
    } else {
      props.onGuideRemove?.(drag.index);
    }
  };

  /* ---------------- 尺寸 ---------------- */

  onMount(() => {
    const element = container;
    if (element === undefined) return;
    const observer = new ResizeObserver(() => {
      const rect = element.getBoundingClientRect();
      const size = { width: Math.round(rect.width), height: Math.round(rect.height) };
      const previous = viewSize();
      setViewSize(size);
      // 首次拿到尺寸时自动适应窗口——否则用户打开文档看到的是左上角一小块
      if (previous.width === 0 && size.width > 0) {
        update(fitToWindow(props.documentSize, size));
      }
    });
    observer.observe(element);
    onCleanup(() => {
      observer.disconnect();
    });
  });

  // 文档尺寸变化（新建/打开了另一份文档）时重新适应窗口。
  // 用手动比较而不是依赖 effect 的首次运行：首次适应已由 ResizeObserver 负责
  let fittedFor = '';
  createEffect(() => {
    const size = props.documentSize;
    const view = viewSize();
    const signature = `${size.width}x${size.height}`;
    if (view.width === 0) return;
    if (fittedFor === '') {
      fittedFor = signature; // 首帧由 ResizeObserver 适应，这里只记账
      return;
    }
    if (signature !== fittedFor) {
      fittedFor = signature;
      update(fitToWindow(size, view));
    }
  });

  /* ---------------- 键盘 ---------------- */

  onMount(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.code === 'Space' && !event.repeat) {
        physicalSpaceDown = true;
        syncHandSpace();
        // 不阻止默认行为的话，空格会滚动页面
        event.preventDefault();
        return;
      }
      const command = viewCommandForKey(event);
      if (command === null) return;
      // Ctrl+0 / Ctrl+1 在浏览器里本身有缩放含义，必须拦下来
      event.preventDefault();
      update(applyViewCommand(viewport(), command, context()));
    };

    const onKeyUp = (event: KeyboardEvent): void => {
      if (event.code !== 'Space') return;
      physicalSpaceDown = false;
      syncHandSpace();
    };

    // 挂在 window 上：画布本身未必有焦点，而这些是全局的视图快捷键
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    onCleanup(() => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    });
  });

  /* ---------------- 指针 ---------------- */

  /** 事件坐标 → 文档坐标 + 修饰键 + 压感样本流，转发给工具层。 */
  const forwardToTool = (
    phase: 'down' | 'move' | 'up' | 'double',
    event: PointerEvent | MouseEvent,
  ): void => {
    if (props.onToolPointer === undefined) return;
    const toDoc = (e: { clientX: number; clientY: number }): ReturnType<typeof screenToDocument> =>
      screenToDocument(viewport(), toLocal(e));
    // move 时展开 coalesced：数位板的中间采样点都在里面，不展开快笔画会成折线
    const usePressure = 'pointerType' in event && event.pointerType === 'pen';
    const raw =
      phase === 'move' && 'getCoalescedEvents' in event && event.getCoalescedEvents().length > 0
        ? event.getCoalescedEvents()
        : [event];
    const samples = raw.map((e) => ({
      ...toDoc(e),
      pressure: usePressure && 'pressure' in e ? (e as PointerEvent).pressure : null,
      timeMs: e.timeStamp,
    }));
    props.onToolPointer(phase, toDoc(event), { shiftKey: event.shiftKey, altKey: event.altKey }, samples);
  };

  const onPointerDown = (event: PointerEvent): void => {
    if (hand.pointerDown(toLocal(event))) {
      setHandActive(true);
      // 捕获指针，拖到画布外面也能继续平移
      (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
      event.preventDefault();
      return;
    }
    if (props.zoomToolActive === true) {
      // 缩放工具：点哪就以哪为锚点缩放一档——这是视口操作，不是内容编辑，
      // 直接在这里处理（跟抓手同一个道理），不走 onToolPointer 那条给
      // 内容编辑工具用的文档坐标管线。Alt 按下时方向反过来，跟 PS 一致。
      const wantOut = props.zoomDirection === 'out';
      const effectiveOut = event.altKey ? !wantOut : wantOut;
      const anchor = toLocal(event);
      update(
        applyViewCommand(viewport(), { kind: effectiveOut ? 'zoomOut' : 'zoomIn', anchor }, context()),
      );
      return;
    }
    // 抓手/缩放都不管的指针交给工具
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    forwardToTool('down', event);
  };

  const onPointerMove = (event: PointerEvent): void => {
    setHoverLocal(toLocal(event));
    const delta = hand.pointerMove(toLocal(event));
    if (delta !== null) {
      update(applyViewCommand(viewport(), { kind: 'pan', ...delta }, context()));
      return;
    }
    if (!hand.active) forwardToTool('move', event);
  };

  const onPointerLeave = (): void => {
    setHoverLocal(null);
  };

  const onPointerUp = (event: PointerEvent): void => {
    if (hand.dragging) {
      hand.pointerUp();
      setHandActive(hand.active);
      (event.currentTarget as HTMLElement).releasePointerCapture(event.pointerId);
      return;
    }
    forwardToTool('up', event);
  };

  const onDblClick = (event: MouseEvent): void => {
    if (!hand.active) forwardToTool('double', event);
  };

  const onWheel = (event: WheelEvent): void => {
    // 一律拦下：不拦的话 Ctrl+滚轮会触发浏览器自身的页面缩放
    event.preventDefault();
    const command = viewCommandForWheel(event, toLocal(event));
    update(applyViewCommand(viewport(), command, context()));
  };

  /* ---------------- 绘制 ---------------- */

  createEffect(() => {
    const size = viewSize();
    const current = viewport();
    void props.renderVersion; // 内容版本：读取即建立依赖
    const surface = canvas;
    if (surface === undefined || size.width === 0 || size.height === 0) return;

    // 按设备像素比设置后备缓冲，否则高分屏上一切都是糊的
    const dpr = window.devicePixelRatio || 1;
    surface.width = Math.round(size.width * dpr);
    surface.height = Math.round(size.height * dpr);
    surface.style.width = `${size.width}px`;
    surface.style.height = `${size.height}px`;

    if (props.onRender?.(surface, current, size) === true) return;

    drawPlaceholder(surface, props.documentSize, current, dpr);
  });

  // 标尺跟主画布分开一个 effect：它俩的重绘代价差一个数量级
  // （标尺就画几十条线+文字，没必要绑到主画布那份 dpr 后备缓冲逻辑上）。
  createEffect(() => {
    if (props.showRulers === false) return;
    const size = viewSize();
    const current = viewport();
    const hover = hoverLocal();
    if (size.width === 0 || size.height === 0) return;
    if (rulerH !== undefined) drawRuler(rulerH, 'h', size.width, current, hover);
    if (rulerV !== undefined) drawRuler(rulerV, 'v', size.height, current, hover);
  });

  const rulersOn = (): boolean => props.showRulers !== false;

  return (
    <div
      class={styles.root}
      style={{
        'grid-template-columns': rulersOn() ? `${RULER_SIZE}px 1fr` : '1fr',
        'grid-template-rows': rulersOn() ? `${RULER_SIZE}px 1fr` : '1fr',
      }}
    >
      <Show when={rulersOn()}>
        <div class={styles.corner} />
        <canvas
          ref={(el) => {
            rulerH = el;
          }}
          class={styles.rulerH}
          data-testid="ruler-horizontal"
          onPointerDown={(event) => {
            onRulerPointerDown('vertical', event);
          }}
          onPointerMove={onGuideDragPointerMove}
          onPointerUp={onGuideDragPointerUp}
          onPointerCancel={onGuideDragPointerUp}
        />
        <canvas
          ref={(el) => {
            rulerV = el;
          }}
          class={styles.rulerV}
          data-testid="ruler-vertical"
          onPointerDown={(event) => {
            onRulerPointerDown('horizontal', event);
          }}
          onPointerMove={onGuideDragPointerMove}
          onPointerUp={onGuideDragPointerUp}
          onPointerCancel={onGuideDragPointerUp}
        />
      </Show>
      <div
        ref={(el) => {
          container = el;
        }}
        class={styles.container}
        classList={{ [styles.grabbing!]: handActive() }}
        style={{ cursor: handActive() ? undefined : props.cursor }}
        data-testid="canvas-view"
        data-hand={handActive() ? 'true' : 'false'}
        // 视口状态放在 DOM 属性上，端到端测试直接读。比挂 window 全局探针干净：
        // 不需要区分构建模式，生产环境也就多几个字符
        data-viewport={serializeViewport(viewport())}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerLeave={onPointerLeave}
        onDblClick={onDblClick}
        onWheel={onWheel}
        onContextMenu={(event) => {
          if (props.onContextMenu === undefined) return;
          event.preventDefault();
          props.onContextMenu({ x: event.clientX, y: event.clientY });
        }}
      >
        <canvas
          ref={(el) => {
            canvas = el;
          }}
          class={styles.canvas}
          data-testid="canvas-surface"
        />
        <div class={styles.readout} data-testid="viewport-readout">
          {/* 状态栏要读这几个数，做成 DOM 而不是画进 canvas —— e2e 才能直接断言 */}
          <span data-testid="zoom-level">{Math.round(viewport().scale * 100)}%</span>
        </div>
        <For each={props.guides ?? []}>
          {(guide, index) => {
            const screenPos = (): number => {
              const point =
                guide.axis === 'vertical'
                  ? documentToScreen(viewport(), { x: guide.position, y: 0 }).x
                  : documentToScreen(viewport(), { x: 0, y: guide.position }).y;
              return point;
            };
            const locked = (): boolean => props.guidesLocked === true || guide.locked;
            return (
              <div
                class={guide.axis === 'vertical' ? styles.guideLineV : styles.guideLineH}
                classList={{ [styles.guideLineLocked!]: locked() }}
                style={
                  guide.axis === 'vertical'
                    ? { left: `${screenPos()}px` }
                    : { top: `${screenPos()}px` }
                }
                data-testid="guide-line"
                data-axis={guide.axis}
                data-locked={locked() ? 'true' : 'false'}
                onPointerDown={(event) => {
                  onGuideLinePointerDown(index(), guide, event);
                }}
                onPointerMove={onGuideDragPointerMove}
                onPointerUp={onGuideDragPointerUp}
                onPointerCancel={onGuideDragPointerUp}
              />
            );
          }}
        </For>
        <Show when={guideDrag() !== null}>
          {(() => {
            const drag = guideDrag();
            if (drag === null) return null;
            return (
              <div
                class={styles.guideDragPreview}
                data-testid="guide-drag-preview"
                style={
                  drag.axis === 'vertical'
                    ? { left: `${drag.screenPos}px`, top: 0, 'inline-size': '1px', 'block-size': '100%' }
                    : { top: `${drag.screenPos}px`, left: 0, 'block-size': '1px', 'inline-size': '100%' }
                }
              />
            );
          })()}
        </Show>
      </div>
    </div>
  );
}

/**
 * 画一条标尺（水平或垂直）。刻度定位用 `documentToScreen` 逐点投影
 * （文件头注释解释了旋转视图下的已知简化）。
 */
function drawRuler(
  canvasEl: HTMLCanvasElement,
  orientation: 'h' | 'v',
  length: number,
  viewport: Viewport,
  hover: { x: number; y: number } | null,
): void {
  const ctx = canvasEl.getContext('2d');
  if (ctx === null || length <= 0) return;
  const dpr = window.devicePixelRatio || 1;
  const w = orientation === 'h' ? length : RULER_SIZE;
  const h = orientation === 'h' ? RULER_SIZE : length;
  canvasEl.width = Math.round(w * dpr);
  canvasEl.height = Math.round(h * dpr);
  canvasEl.style.width = `${w}px`;
  canvasEl.style.height = `${h}px`;

  const style = getComputedStyle(canvasEl);
  const token = (name: string, fallback: string): string => {
    const value = style.getPropertyValue(name).trim();
    return value === '' ? fallback : value;
  };
  const bg = token('--color-surface-1', 'dimgray');
  const lineColor = token('--color-border-strong', 'gray');
  const textColor = token('--color-text-muted', 'darkgray');
  const accent = token('--color-accent', 'dodgerblue');
  const fontSizeFallback = '11'; // 分开拼接：写成字面量 "11px" 会被 tokens 扫描器当成硬编码值
  const fontSize = token('--font-size-1', `${fontSizeFallback}px`);

  ctx.save();
  ctx.scale(dpr, dpr);
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, w, h);

  const majorStep = rulerMajorStep(viewport.scale);
  const minorStep = majorStep / 5;
  const docAt = (screenPos: number): number =>
    orientation === 'h'
      ? screenToDocument(viewport, { x: screenPos, y: 0 }).x
      : screenToDocument(viewport, { x: 0, y: screenPos }).y;
  const docLo = Math.min(docAt(0), docAt(length));
  const docHi = Math.max(docAt(0), docAt(length));
  const startTick = Math.floor(docLo / minorStep) * minorStep;

  ctx.strokeStyle = lineColor;
  ctx.fillStyle = textColor;
  ctx.font = `${fontSize} sans-serif`;
  ctx.lineWidth = 1;
  ctx.textBaseline = 'alphabetic';

  for (let v = startTick; v <= docHi; v += minorStep) {
    const rounded = Math.round(v / minorStep) * minorStep; // 累加浮点误差可能让"整刻度"偏几厘，先吸附回去
    const isMajor = Math.abs(Math.round(rounded / majorStep) * majorStep - rounded) < minorStep / 2;
    const point = orientation === 'h' ? { x: rounded, y: 0 } : { x: 0, y: rounded };
    const screen = documentToScreen(viewport, point);
    const pos = orientation === 'h' ? screen.x : screen.y;
    if (pos < -1 || pos > length + 1) continue;
    const tickLen = isMajor ? RULER_SIZE * 0.55 : RULER_SIZE * 0.25;
    ctx.beginPath();
    if (orientation === 'h') {
      ctx.moveTo(pos + 0.5, RULER_SIZE);
      ctx.lineTo(pos + 0.5, RULER_SIZE - tickLen);
    } else {
      ctx.moveTo(RULER_SIZE, pos + 0.5);
      ctx.lineTo(RULER_SIZE - tickLen, pos + 0.5);
    }
    ctx.stroke();
    if (isMajor) {
      const label = String(Math.round(rounded));
      if (orientation === 'h') {
        ctx.fillText(label, pos + 2, 9);
      } else {
        ctx.save();
        ctx.translate(9, pos - 2);
        ctx.rotate(-Math.PI / 2);
        ctx.fillText(label, 0, 0);
        ctx.restore();
      }
    }
  }

  // 光标位置指示线——跟手，画布空间挪到哪根线就跟到哪
  if (hover !== null) {
    const pos = orientation === 'h' ? hover.x : hover.y;
    if (pos >= 0 && pos <= length) {
      ctx.strokeStyle = accent;
      ctx.beginPath();
      if (orientation === 'h') {
        ctx.moveTo(pos + 0.5, 0);
        ctx.lineTo(pos + 0.5, RULER_SIZE);
      } else {
        ctx.moveTo(0, pos + 0.5);
        ctx.lineTo(RULER_SIZE, pos + 0.5);
      }
      ctx.stroke();
    }
  }
  ctx.restore();
}

/**
 * 占位绘制：棋盘格背景 + 文档边框。
 *
 * 存在的意义不只是"好看一点"：它让视口交互在渲染管线接上之前就能被
 * 端到端测试——文档矩形的位置与大小直接反映了视口变换是否正确。
 */
function drawPlaceholder(
  canvas: HTMLCanvasElement,
  documentSize: Size,
  viewport: Viewport,
  dpr: number,
): void {
  const ctx = canvas.getContext('2d');
  if (ctx === null) return;

  // 颜色从设计 token 读，不写字面量（§7.2，test/ui/tokens.test.ts 会扫）。
  // 这不只是为了过检查：主题切换时画布跟着变，硬编码就变不了。
  //
  // **只取一次 computed style**：getComputedStyle 会强制样式重算，
  // 每帧调四次足以把 60fps 压到 30。拿到之后 getPropertyValue 很便宜。
  const style = getComputedStyle(canvas);
  const token = (name: string, fallback: string): string => {
    const value = style.getPropertyValue(name).trim();
    return value === '' ? fallback : value;
  };

  ctx.save();
  ctx.scale(dpr, dpr);
  const width = canvas.width / dpr;
  const height = canvas.height / dpr;

  ctx.fillStyle = token('--color-canvas-bg', 'gray');
  ctx.fillRect(0, 0, width, height);

  const bounds = documentBoundsOnScreen(documentSize, viewport);

  // 棋盘格只画在文档范围内，表示透明区域。
  //
  // 用 pattern 一次填充，不是逐格 fillRect：视口 800×600、格子 8px 时，
  // 逐格要画上万次，实测把帧率压到 50 以下。pattern 由 GPU 平铺，是一次绘制。
  const checkerA = token('--color-checker-a', 'white');
  const checkerB = token('--color-checker-b', 'silver');
  const pattern = checkerPattern(ctx, checkerA, checkerB);
  ctx.save();
  ctx.beginPath();
  ctx.rect(bounds.x, bounds.y, bounds.width, bounds.height);
  ctx.clip();
  if (pattern === null) {
    ctx.fillStyle = checkerA;
    ctx.fillRect(bounds.x, bounds.y, bounds.width, bounds.height);
  } else {
    ctx.fillStyle = pattern;
    ctx.fillRect(bounds.x, bounds.y, bounds.width, bounds.height);
  }
  ctx.restore();

  ctx.strokeStyle = token('--color-accent', 'blue');
  ctx.lineWidth = 1;
  // 半像素偏移让 1 像素的线落在像素中心上，否则会糊成两像素
  ctx.strokeRect(bounds.x + 0.5, bounds.y + 0.5, bounds.width - 1, bounds.height - 1);
  ctx.restore();
}
